// src/components/SettingsModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Building2 } from 'lucide-react';
import { useCaps } from '../contexts/CapabilitiesContext';
import { setPreferredOrgId } from '../utils/preferredOrg';
import { getCookie } from '../utils/utils';

interface Currency {
  code: string;
  name: string;
  symbol: string;
  exchange_rate: number;
  is_active: boolean;
}

interface SwitchableOrg {
  id: number;
  name: string;
  is_account_manager?: boolean;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCurrency: string;
  onCurrencyChange: (currency: string) => void;
  currentOrgId?: number | null;
  userId?: number | null;
  section?: 'currency' | 'organization';
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  selectedCurrency,
  onCurrencyChange,
  currentOrgId,
  userId,
  section = 'currency',
}) => {
  const { can } = useCaps();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [switchableOrgs, setSwitchableOrgs] = useState<SwitchableOrg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (section === 'currency') fetchCurrencies();
      if (section === 'organization' && can('organizations_selection')) {
        fetchSwitchableOrgs();
      }
    }
  }, [isOpen, section, can]);

  const fetchCurrencies = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/currencies/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrencies(data);
      }
    } catch (err) {
      console.error('Error fetching currencies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSwitchableOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const response = await fetch('/api/user/orgs/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data: SwitchableOrg[] = await response.json();
        setSwitchableOrgs(data);
      }
    } catch (err) {
      console.error('Error fetching orgs:', err);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const handleCurrencySelect = (currencyCode: string) => {
    onCurrencyChange(currencyCode);
    onClose();
  };

  const handleOrgSelect = async (orgId: number) => {
    if (orgId === currentOrgId) return;
    setIsSwitchingOrg(true);
    try {
      const csrfToken = getCookie('csrftoken');

      const response = await fetch('/api/user/profile/', {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ org: orgId }),
      });

      if (response.ok) {
        if (userId != null) {
          setPreferredOrgId(userId, orgId);
        }
        onClose();
        window.location.reload();
      }
    } catch (err) {
      console.error('Error switching org:', err);
    } finally {
      setIsSwitchingOrg(false);
    }
  };

  const showOrg = section === 'organization';
  const showCurrency = false; // Hidden: orgs now use a single billing currency. Was: section === 'currency'
  const canSelectOrgs = can('organizations_selection');
  const hasOrgSwap = canSelectOrgs && switchableOrgs.length > 0;

  const sortedOrgs = [...switchableOrgs].sort((a, b) => {
    const aIsCurrent = a.id === currentOrgId;
    const bIsCurrent = b.id === currentOrgId;
    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;
    return a.name.localeCompare(b.name);
  });

  if (!isOpen) return null;

  const modalTitle = section === 'currency' ? 'Currency' : 'Organization';

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scaleIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-black">{modalTitle}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Organization (Catalyx users only) */}
          {showOrg && (
            <div>
              {hasOrgSwap ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Select organization
                    </h3>
                  </div>
                  {canSelectOrgs && (
                    isLoadingOrgs ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading organizations...</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {sortedOrgs.map((org) => (
                          <div key={org.id} className="flex items-center gap-2">
                            <button
                              onClick={() => handleOrgSelect(org.id)}
                              disabled={isSwitchingOrg}
                              className={`flex-1 flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                                currentOrgId === org.id
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              <span className="text-sm font-medium text-gray-900">{org.name}</span>
                              {currentOrgId === org.id && (
                                <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  You do not have permission to view the organization selection menu.
                </p>
              )}
            </div>
          )}

          {/* Currency selector hidden — orgs have a single billing currency.
          {showCurrency && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Select currency</h3>
              ...
            </div>
          )}
          */}
          {false && showCurrency && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Select currency</h3>
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading currencies...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {currencies.map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => handleCurrencySelect(currency.code)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                      selectedCurrency === currency.code
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-semibold">{currency.symbol}</span>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">{currency.name}</p>
                        <p className="text-xs text-gray-500">{currency.code}</p>
                      </div>
                    </div>
                    {selectedCurrency === currency.code && (
                      <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default SettingsModal;
